import { prepareSavePointPayload } from "@/hooks/create-page/utils/formStateHelpers";
import { FormPersistenceService } from "@/services/formPersistenceService";
import type { CreatePageState } from "@/stores/createPageStore";
import {
  clearCreditPurchaseAfterAuth,
  CREDIT_RESUME_ID_PARAM,
  hasCreditPurchaseAfterAuth,
  rememberCreditPurchaseAfterAuth,
} from "@/utils/creditAuthIntentPersistence";
import {
  beginTrackedCreateAuthHandoff,
  cancelTrackedCreateAuthHandoff,
} from "@/utils/createAuthHandoffLifecycle";
import { CREATE_AUTH_HANDOFF_PARAM } from "@/utils/createAuthHandoffPersistence";
import { sanitizeCreateReturnUrl } from "@/utils/creditCheckoutReturnUrl";
import { createResumeIntentKey } from "@/utils/createResumeIntentKey";
import SavePoint from "@/utils/savePoint";

export const CREDIT_AUTH_HANDOFF_ERROR =
  "Your dress is still here. Allow site data, then try again.";

export interface PreparedCreditAuthHandoff {
  intentId: string;
  returnPath: string;
}

function getCreditResumeKey(state: CreatePageState): string | null {
  return createResumeIntentKey({
    ticketId: state.ticket.id,
    frontImagePath: state.generation.images.front.path,
    agentSessionId: state.agent.sessionId,
    pendingSubmittedMessageId: state.agent.pendingSubmittedMessageId,
  });
}

function clearExactCreditHandoff(intentId?: string | null): void {
  if (!intentId) return;
  clearCreditPurchaseAfterAuth(intentId);
  SavePoint.clear(intentId);
}

function persistExactCreditHandoff({
  state,
  preferredIntentId,
  credits,
  offerToken,
}: {
  state: CreatePageState;
  preferredIntentId?: string | null;
  credits?: number;
  offerToken?: string;
}): string | null {
  if (credits !== undefined && (!Number.isInteger(credits) || credits <= 0)) {
    return null;
  }

  const resumeKey = getCreditResumeKey(state);
  if (!resumeKey) return null;
  const intentId =
    credits !== undefined
      ? rememberCreditPurchaseAfterAuth(
          credits,
          resumeKey,
          preferredIntentId,
          offerToken
        )
      : preferredIntentId;
  if (!intentId) return null;

  FormPersistenceService.saveState({
    data: prepareSavePointPayload(state, 1),
    source: "auth_redirect",
    intentId,
  });

  // Email confirmation and OAuth commonly return in a new tab. Refuse to
  // leave this dress unless its exact URL-scoped state is durably readable,
  // rather than merely surviving in this tab's sessionStorage.
  const durableSavePoint = SavePoint.getDurableSavePoint(intentId);
  const hasExactPackIntent =
    credits !== undefined
      ? hasCreditPurchaseAfterAuth(intentId, credits, resumeKey)
      : true;
  if (
    durableSavePoint?.version !== 1 ||
    durableSavePoint.intentId !== intentId ||
    durableSavePoint.source !== "auth_redirect" ||
    !durableSavePoint.data ||
    !hasExactPackIntent
  ) {
    clearExactCreditHandoff(intentId);
    return null;
  }

  return intentId;
}

function buildCreditReturnPath({
  currentHref,
  intentId,
  credits,
  authHandoffId,
}: {
  currentHref: string;
  intentId: string;
  credits?: number;
  authHandoffId?: string | null;
}): string {
  const returnUrl = sanitizeCreateReturnUrl(currentHref);
  if (authHandoffId) {
    returnUrl.searchParams.set(CREATE_AUTH_HANDOFF_PARAM, authHandoffId);
  }
  if (credits !== undefined) {
    returnUrl.searchParams.set("credit_intent", "1");
    returnUrl.searchParams.set(CREDIT_RESUME_ID_PARAM, intentId);
    returnUrl.searchParams.set("credit_pack", String(credits));
  } else {
    returnUrl.searchParams.set("credit_gate", "1");
  }
  return returnUrl.pathname + returnUrl.search + returnUrl.hash;
}

/** Prepare the exact continuation used by an in-place anonymous upgrade. */
export function prepareCreditAccountUpgradeHandoff({
  state,
  currentHref,
  credits,
  offerToken,
}: {
  state: CreatePageState;
  currentHref: string;
  credits: number;
  offerToken?: string;
}): PreparedCreditAuthHandoff | null {
  const intentId = persistExactCreditHandoff({ state, credits, offerToken });
  if (!intentId) return null;

  try {
    return {
      intentId,
      returnPath: buildCreditReturnPath({ currentHref, intentId, credits }),
    };
  } catch {
    clearExactCreditHandoff(intentId);
    return null;
  }
}

/** Prepare the exact continuation used by existing-account or Google sign-in. */
export function prepareCreditSignInHandoff({
  state,
  currentHref,
  sourceUserId,
  credits,
  offerToken,
}: {
  state: CreatePageState;
  currentHref: string;
  sourceUserId?: string | null;
  credits?: number;
  offerToken?: string;
}): PreparedCreditAuthHandoff | null {
  const authHandoffId = beginTrackedCreateAuthHandoff(sourceUserId);
  if (!authHandoffId) return null;

  const intentId = persistExactCreditHandoff({
    state,
    preferredIntentId: authHandoffId,
    credits,
    offerToken,
  });
  if (!intentId) {
    cancelTrackedCreateAuthHandoff(authHandoffId);
    return null;
  }

  try {
    return {
      intentId,
      returnPath: buildCreditReturnPath({
        currentHref,
        intentId,
        credits,
        authHandoffId,
      }),
    };
  } catch {
    cancelTrackedCreateAuthHandoff(authHandoffId);
    return null;
  }
}
